import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "@src/API.js";
import CustomButton from "../Controls/CustomButton";
import { toast } from "react-toastify";

const LEVEL_NAMES = {
  1: "Beginner",
  2: "Junior",
  3: "Intermediate",
  4: "Senior",
  5: "Guru",
};

export default function ScreeningInviteLanding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setError("Missing screening token");
        return;
      }
      const resp = await API.getAsync(`/Skill/Test/Screening/${encodeURIComponent(token)}`);
      if (cancelled) return;
      if (resp?.ok) setMeta(resp.jsonData);
      else setError(resp?.jsonData?.message || "Invalid or expired screening link");
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function start() {
    if (!token || busy) return;
    setBusy(true);
    try {
      const resp = await API.postAsync(`/Skill/Test/Screening/${encodeURIComponent(token)}/Start`, {});
      if (!resp?.ok) {
        toast.error(resp?.jsonData?.message || "Could not start screening");
        setError(resp?.jsonData?.message || "Could not start screening");
        return;
      }
      const testId = resp.jsonData?.testId ?? resp.jsonData?.TestId;
      if (!testId) {
        toast.error("Start succeeded but test id missing");
        return;
      }
      window.sessionStorage.setItem("ScreeningToken", token);
      navigate(`/skillTest/${testId}?token=${encodeURIComponent(token)}`);
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <div className="pagebody">
        <div className="tabbody">
          <h1>Screening invite</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!meta) return <p className="pagebody">Loading invite…</p>;

  const status = meta.status || meta.Status;
  const skillName = meta.skillName || meta.SkillName || "skill";
  const mode = Number(meta.mode ?? meta.Mode);
  const level = meta.skillLevel ?? meta.SkillLevel;

  return (
    <div className="pagebody">
      <div className="tabbody">
        <h1>Skill screening</h1>
        <p className="byline">
          {skillName} · {mode === 2 ? "Level discovery (Type 2)" : "Certification (Type 1)"}
          {mode === 1 && level ? ` · ${LEVEL_NAMES[level] || level}` : ""}
        </p>
        <p>Status: {status}</p>
        {status === "Completed" ? (
          <p>This screening is already complete. You can close this page.</p>
        ) : status === "Expired" ? (
          <p>This invite has expired. Ask the recruiter for a new link.</p>
        ) : (
          <>
            <p>
              You have been invited to take a skill screening. Click Start to begin. No account login
              is required for this link.
            </p>
            <CustomButton type="button" variant="contained" color="primary" disabled={busy} onClick={start}>
              {status === "InProgress" ? "Continue test" : "Start screening"}
            </CustomButton>
          </>
        )}
      </div>
    </div>
  );
}
